import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, BookOpen, FileText, Quote } from "lucide-react";
import { SAMPLES, FILTERS, BEFORE_AFTER } from "@/data/content";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SampleModal = ({ sample, onClose }) => (
  <AnimatePresence>
    {sample && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-8"
        onClick={onClose}
        data-testid="sample-modal-backdrop"
      >
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="bg-paper border border-line w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-[0_40px_80px_-30px_rgba(26,24,21,0.5)]"
          data-testid="sample-reader-modal"
        >
          <div className="sticky top-0 bg-paper/90 backdrop-blur-md border-b border-line px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-wine">{sample.client}</span>
              <span className="text-subtle">/</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">{sample.type}</span>
            </div>
            <button onClick={onClose} className="p-2 hover:text-wine transition-colors duration-300" data-testid="close-sample-modal-button" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="px-8 py-10">
            <h3 className="font-serif text-2xl sm:text-3xl tracking-tight leading-[1.15]">{sample.title}</h3>
            <p className="mt-6 font-serif italic text-xl text-wine leading-snug border-l-2 border-wine pl-5">
              "{sample.hook}"
            </p>
            <div className="mt-8 flex flex-col gap-5">
              {sample.body.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-ink/85">{p}</p>
              ))}
            </div>
            <div className="mt-10 grid sm:grid-cols-3 gap-6 border-t border-line pt-8">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-1">Length</div>
                <div className="font-serif text-lg">{sample.words} words</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-1">Format</div>
                <div className="font-serif text-lg">{sample.type}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-1">Client</div>
                <div className="font-serif text-lg">{sample.client}</div>
              </div>
            </div>
            <div className="mt-8 bg-paperdeep border border-line p-6 flex gap-4">
              <Quote size={18} className="text-amber shrink-0 mt-1" />
              <p className="font-serif italic text-lg leading-snug text-subtle">{sample.note}</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const BeforeAfter = () => {
  const [showAfter, setShowAfter] = useState(true);
  return (
    <motion.div
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className="mt-24 border border-line bg-cream"
      data-testid="before-after-block"
    >
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-line p-8 sm:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-4">Optimization, in the open</p>
          <h3 className="font-serif text-2xl sm:text-3xl tracking-tight">{BEFORE_AFTER.title}</h3>
          <p className="mt-4 text-sm leading-relaxed text-subtle">{BEFORE_AFTER.context}</p>
          <div className="mt-8 inline-flex rounded-full border border-ink/20 p-1" role="tablist">
            {[
              { key: false, label: "Before", testid: "before-toggle-button" },
              { key: true, label: "After", testid: "after-toggle-button" },
            ].map((t) => (
              <button
                key={t.label}
                role="tab"
                aria-selected={showAfter === t.key}
                onClick={() => setShowAfter(t.key)}
                data-testid={t.testid}
                className={`px-5 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                  showAfter === t.key ? "bg-ink text-paper" : "text-subtle hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-8 p-8 sm:p-10 relative min-h-[280px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={showAfter ? "after" : "before"}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4 }}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-5" data-testid="before-after-label">
                {showAfter ? BEFORE_AFTER.after.label : BEFORE_AFTER.before.label}
              </p>
              <p className="font-serif text-xl sm:text-2xl leading-relaxed text-ink" data-testid="before-after-text">
                {showAfter ? BEFORE_AFTER.after.text : BEFORE_AFTER.before.text}
              </p>
              {showAfter && (
                <p className="mt-6 text-sm leading-relaxed text-wine border-l-2 border-wine pl-4">
                  {BEFORE_AFTER.rationale}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export const Samples = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const visible = useMemo(
    () => (filter === "All" ? SAMPLES : SAMPLES.filter((s) => s.category === filter)),
    [filter]
  );

  return (
    <section id="work" className="py-24 sm:py-32 lg:py-40 bg-paperdeep/60 border-y border-line" data-testid="samples-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0} className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-6">
              Selected writing samples
            </motion.p>
            <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15] max-w-xl">
              Two brands. Seven channels. <em className="text-wine">One voice discipline.</em>
            </motion.h2>
          </div>
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className="flex flex-wrap gap-2" data-testid="sample-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`sample-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.16em] border transition-colors duration-300 ${
                  filter === f
                    ? "bg-wine text-paper border-wine"
                    : "border-ink/20 text-subtle hover:border-wine hover:text-wine"
                }`}
              >
                {f}
              </button>
            ))}
          </motion.div>
        </div>

        <motion.div layout className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="samples-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((s, i) => (
              <motion.article
                layout
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-cream border border-line p-7 flex flex-col hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(26,24,21,0.35)] transition-[transform,box-shadow] duration-500"
                data-testid={`writing-sample-card-${s.id}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-wine">{s.client}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-subtle">{s.category}</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl leading-[1.25] tracking-tight">{s.title}</h3>
                <p className="mt-3 font-serif italic text-lg text-wine/90 leading-snug">"{s.hook}"</p>
                <p className="mt-4 text-sm leading-relaxed text-subtle line-clamp-3 flex-1">{s.excerpt}</p>
                <button
                  onClick={() => setActive(s)}
                  data-testid={`open-sample-modal-button-${s.id}`}
                  className="mt-7 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink group-hover:text-wine transition-colors duration-300 self-start"
                >
                  <BookOpen size={14} />
                  Read the excerpt
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <BeforeAfter />
      </div>

      <SampleModal sample={active} onClose={() => setActive(null)} />
    </section>
  );
};

export const SamplesFileIcon = FileText;
